import axios from "axios"
import { useCallback, useMemo } from "react"
import { selectSelectedLocale } from "../../modules/localization/localizationSelectors"
import { useAppLocalization } from "../../modules/localization/components/AppLocalizationProvider"
import { selectStandardTimeout } from "../../modules/config/configSelectors"
import { selectAuthToken } from "../../modules/login/loginSelectors"
import { useEnvironment } from "../../modules/environment/envHooks"
import { useAppSelector } from "../../modules/store/storeHooks"
import { isNonEmptyString, isObjectWithProp } from "../../lib/lang"
import { useApiErrors } from "./useApiErrors"
import { Logger } from "../../lib/logging"
import { ApiError } from "./errorTypes"

type AxiosCreateOptions = Readonly<{
    baseURL: string
    locale: string
    authToken: string | undefined
    timeoutInMillis?: number
    errorHandler?: (error: unknown) => void
    accept?: string
}>

export const CLIENT_TYPE = "gopay-manager"
const DEFAULT_TIMEOUT_MILLIS = 25000

export function createAxiosInstance(options: AxiosCreateOptions) {
    const { baseURL, locale, authToken, timeoutInMillis = DEFAULT_TIMEOUT_MILLIS, errorHandler, accept } = options

    const axiosInstance = axios.create({
        baseURL,
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Client-Type": CLIENT_TYPE,
        },
        timeout: timeoutInMillis,
    })

    axiosInstance.interceptors.request.use((config) => {
        if (config.headers) {
            config.headers["Accept-Language"] = locale
            if (accept) config.headers["Accept"] = accept
            if (authToken) config.headers["X-Api-Key"] = authToken
        }

        return config
    })

    if (errorHandler) {
        axiosInstance.interceptors.response.use((response) => response, errorHandler)
    }

    return axiosInstance
}

type AxiosOptions = Readonly<{
    baseURL?: string
    authenticated?: boolean
    performErrorHandling?: boolean
    timeoutInMillis?: number
    accept?: string
}>

export function useAxios(options: AxiosOptions) {
    const {
        baseURL: baseUrlOverride,
        authenticated = true,
        performErrorHandling = true,
        timeoutInMillis,
        accept,
    } = options

    const logger = new Logger("api")

    const locale = useAppSelector(selectSelectedLocale)
    const authToken = useAppSelector(selectAuthToken)
    const standardTimeout = useAppSelector(selectStandardTimeout)

    const { currentEnv } = useEnvironment()
    const { l10n } = useAppLocalization()
    const { getAxiosErrorTitle, getAxiosErrorMessage } = useApiErrors()

    // Handle timeout based on client configuration or fallback to default
    const effectiveTimeoutInMillis = timeoutInMillis || standardTimeout || DEFAULT_TIMEOUT_MILLIS

    logger.info(`Using timeout: ${effectiveTimeoutInMillis}ms`)

    // Error handler for Axios requests
    const errorHandler = useCallback(
        (error: unknown) => {
            if (axios.isAxiosError(error)) {
                const title = getAxiosErrorTitle(error)
                const message = getAxiosErrorMessage(error)

                const apiStatus =
                    error.response &&
                    isObjectWithProp(error.response.data, "status") &&
                    isNonEmptyString(error.response.data.status)
                        ? error.response.data.status
                        : undefined

                const hasApiMessage =
                    error.response &&
                    error.response.data &&
                    isObjectWithProp(error.response.data, "displayMessage") &&
                    typeof error.response.data.displayMessage === "string"

                throw new ApiError(
                    error.code ?? "E_NETWORK_ERROR",
                    title,
                    message,
                    hasApiMessage,
                    error.response?.status,
                    error.response?.statusText,
                    apiStatus,
                    error.cause,
                    error.response,
                )
            }

            // Response will not be available in case of network errors etc.
            const cause = error instanceof Error ? error : undefined

            throw new ApiError(
                "E_NETWORK_ERROR",
                l10n.getString("api-call-error-title-network-error"),
                l10n.getString("api-call-error-unknown-error"),
                false,
                undefined,
                undefined,
                undefined,
                cause,
            )
        },
        [axios, getAxiosErrorTitle, getAxiosErrorMessage, l10n],
    )

    // Override baseURL or use environment's API base URL
    const baseURL = baseUrlOverride ?? currentEnv.api.baseUrl

    logger.info(`Using API endpoint: ${baseURL} [default: ${currentEnv.api.baseUrl}, override: ${baseUrlOverride}]`)

    const axiosInstance = useMemo(() => {
        return createAxiosInstance({
            baseURL,
            locale,
            authToken: authenticated ? authToken : undefined,
            timeoutInMillis: effectiveTimeoutInMillis,
            errorHandler: performErrorHandling ? errorHandler : undefined,
            accept,
        })
    }, [
        baseURL,
        locale,
        authenticated,
        authToken,
        effectiveTimeoutInMillis,
        performErrorHandling,
        errorHandler,
        accept,
    ])

    return axiosInstance
}
